import { cn } from '@/lib/utils';
import { MessageContext } from 'contexts/MessageContext';
import { memo, useContext } from 'react';

import {
  type IAction,
  type IMessageElement,
  type IStep
} from '@chainlit/react-client';

import { useLayoutMaxWidth } from 'hooks/useLayoutMaxWidth';
import {
  isStudentMessageStep,
  studentVisibleDescendants
} from '@/lib/stepVisibility';

import { Messages } from '..';
import { AskActionButtons } from './AskActionButtons';
import { AskFileButton } from './AskFileButton';
import { MessageAvatar } from './Avatar';
import { MessageButtons } from './Buttons';
import { MessageContent } from './Content';
import UserMessage from './UserMessage';

interface Props {
  message: IStep;
  elements: IMessageElement[];
  actions: IAction[];
  indent: number;
  isRunning?: boolean;
  isScorable?: boolean;
  scorableRun?: IStep;
}

const Message = memo(
  ({
    message,
    elements,
    actions,
    isRunning,
    indent,
    isScorable,
    scorableRun
  }: Props) => {
    const { allowHtml, latex, onError } = useContext(MessageContext);
    const layoutMaxWidth = useLayoutMaxWidth();
    const isUserMessage = message.type === 'user_message';
    const isStep = !isStudentMessageStep(message);
    // Internal steps remain persisted for the admin audit view, but the
    // student transcript contains only user/assistant messages. Recursing into
    // child messages preserves the assistant's natural-language response.
    if (isStep) {
      const visibleChildren = studentVisibleDescendants(message);
      if (!visibleChildren.length) {
        return null;
      }
      return (
        <Messages
          messages={visibleChildren}
          elements={elements}
          actions={actions}
          indent={indent}
          isRunning={isRunning}
          scorableRun={scorableRun}
        />
      );
    }

    return (
      <>
        <div data-step-type={message.type} className="step py-2">
          <div
            className="flex flex-col"
            style={{
              maxWidth: indent ? '100%' : layoutMaxWidth
            }}
          >
            <div
              className={cn('flex flex-grow pb-2')}
              id={`step-${message.id}`}
            >
              {/* User message is displayed differently */}
              {isUserMessage ? (
                <div className="flex flex-col flex-grow max-w-full">
                  <UserMessage message={message} elements={elements}>
                    <MessageContent
                      elements={[]}
                      message={message}
                      allowHtml={allowHtml}
                      latex={latex}
                    />
                  </UserMessage>
                </div>
              ) : (
                <div className="ai-message group/assistant flex gap-3 w-full">
                  <MessageAvatar
                    author={message.metadata?.avatarName || message.name}
                    isError={message.isError}
                    isLoading={Boolean(isRunning && !message.end)}
                  />
                  <div className="flex flex-col items-start min-w-[150px] flex-grow gap-2">
                    <MessageContent
                      elements={elements}
                      message={message}
                      allowHtml={allowHtml}
                      latex={latex}
                    />

                    <AskFileButton messageId={message.id} onError={onError} />
                    <AskActionButtons
                      actions={actions}
                      messageId={message.id}
                    />

                    <MessageButtons
                      message={message}
                      actions={actions}
                      run={
                        scorableRun && isScorable ? scorableRun : undefined
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Display the child steps if the message is not a step (usually a user message). */}
        {message.steps ? (
          <Messages
            messages={message.steps}
            elements={elements}
            actions={actions}
            indent={indent}
            isRunning={isRunning}
          />
        ) : null}
      </>
    );
  }
);

export { Message };
